import { ApolloError } from 'apollo-client';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { ErrorReporter } from '../error/components/ErrorReporter';
import { ErrorDisplayInfo } from '../error/types/ErrorDisplayInfo';
import { assertUnreachable } from '../utils/TypescriptHelpers';
import { GraphQLError, GraphQLErrorCode } from './GraphQLError';

export function getFirstGraphQLErrorCode(apolloError: ApolloError): GraphQLErrorCode | null {
  // TODO: Revisit the use of this cast when updating graphql/apollo dependencies (types) -- TFS 944332
  // The following dangerous cast SHOULD be safe so long as the locally GraphQLError type stays in sync
  // with the types of errors returned by the relationship service
  const graphQLErrors = apolloError.graphQLErrors as unknown as GraphQLError[] | undefined;
  const firstError: GraphQLError | null | undefined = graphQLErrors ? graphQLErrors[0] : null;
  return getGraphQLErrorCode(firstError);
}

export function getGraphQLErrorCode(graphQLError: GraphQLError | null | undefined): GraphQLErrorCode | null {
  return graphQLError && graphQLError.extensions ? graphQLError.extensions.code : null;
}

export function isFatalError(errorCode: GraphQLErrorCode): boolean {
  return errorCode === 'INGESTION_FAILED' || errorCode === 'INTERNAL_SERVER_ERROR';
}

export function getMessageForErrorCode(errorCode: GraphQLErrorCode | null): string {
  if (!errorCode) {
    return Messages.ErrorWithoutErrorCode();
  }
  switch (errorCode) {
    case 'BACKFILL_RUNNING':
      return Messages.ErrorBackfillRunning();
    case 'FEATURE_DISABLED':
      return Messages.ErrorFeatureDisabled();
    case 'INGESTION_FAILED':
      return Messages.ErrorIngestionFailed();
    case 'INTERNAL_SERVER_ERROR':
      return Messages.ErrorInternalServerError();
    case 'NODE_LIMIT_EXCEEDED':
      return Messages.ErrorNodeLimitExceeded();
    case 'PERMISSIONS_MODE_SWITCHED':
      // don't surface this error to the user, show a generic error
      return Messages.ErrorWithoutErrorCode();
    case 'RATE_EXCEEDED':
      return Messages.ErrorRateExceeded();
    case 'TIME_LIMIT_EXCEEDED':
      return Messages.ErrorTimeLimitExceeded();
    default:
      assertUnreachable(errorCode);
      return '';
  }
}

export function getToastMessageForErrorCode(errorCode: GraphQLErrorCode | null): string {
  switch (errorCode) {
    case 'BACKFILL_RUNNING':
      return Messages.ErrorBackfillRunningToast();
    case 'FEATURE_DISABLED':
      return Messages.ErrorFeatureDisabled();
    case 'NODE_LIMIT_EXCEEDED':
      return Messages.ErrorNodeLimitExceededToast();
    case 'PERMISSIONS_MODE_SWITCHED':
      // in this case, used for logging
      return 'PERMISSIONS_MODE_SWITCHED error occured';
    case 'RATE_EXCEEDED':
      return Messages.ErrorRateExceededToast();
    case 'TIME_LIMIT_EXCEEDED':
      return Messages.ErrorTimeLimitExceededToast();
    default:
      return Messages.ErrorWithoutErrorCode();
  }
}

export function getErrorDisplayInfo(errorCode: GraphQLErrorCode | null): ErrorDisplayInfo {
  const errorMessage = getToastMessageForErrorCode(errorCode);
  return {
    errorCode: errorCode || 'none',
    errorMessage: errorMessage,
  };
}

export function reportGraphQLError(errorCode: GraphQLErrorCode | null, key?: string): React.ReactNode {
  const displayInfo = getErrorDisplayInfo(errorCode);
  return (
    <ErrorReporter
      key={key}
      errorCode={displayInfo.errorCode}
      errorMessage={displayInfo.errorMessage}
    />
  );
}

// Note (drbr): Using the array index as key feels fishy. How can we be certain that the errors will always have the same index,
// even when moving from page to page? Is this "error component" pattern really the right one?
export function reportGraphQLErrors(errors: ReadonlyArray<GraphQLError>): React.ReactNode {
  return errors.map((error, i) => reportGraphQLError(getGraphQLErrorCode(error), `error_${i}`));
}

export function reportNoData(): React.ReactNode {
  return <ErrorReporter errorCode='no_data' errorMessage={Messages.ErrorNoData()} />;
}
